import * as React from 'react';
import apiService, { Project, SubProject } from '../../services/api';
import { RefreshCw, ChevronDown } from 'lucide-react';
import type { SolutionId } from './SolutionsGrid';
import './ProjectSelector.css';

/** Domain/sub_domain filter and labels per solution (matches backend project types). */
const SOLUTION_CONFIG: Record<SolutionId, { domain: string; sub_domain: string; projectLabel: string; subProjectLabel: string }> = {
  master_cross_checks: {
    domain: 'Real Estate',
    sub_domain: 'Master Cross Checks',
    projectLabel: 'Project',
    subProjectLabel: 'Sub-project',
  },
  construction_loan: {
    domain: 'Real Estate',
    sub_domain: 'Construction Loan Agreement',
    projectLabel: 'Project',
    subProjectLabel: 'Sub-project',
  },
};

interface ProjectSelectorProps {
  solutionId: SolutionId;
  onSubProjectSelect: (subProjectId: string) => void;
  onProjectSelect?: (projectId: string) => void;
  onCreateNewSubProject?: () => void;
  selectedSubProjectId?: string;
  selectedProjectId?: string;
  refreshTrigger?: number;
  /** When true (e.g. MCC / CLA guided client create), collapse the panel to free vertical space. */
  guidedSetupActive?: boolean;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  solutionId,
  onSubProjectSelect,
  onProjectSelect,
  onCreateNewSubProject,
  selectedSubProjectId,
  selectedProjectId,
  refreshTrigger,
  guidedSetupActive = false,
}) => {
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [subProjects, setSubProjects] = React.useState<SubProject[]>([]);
  const [internalSelectedProjectId, setInternalSelectedProjectId] = React.useState<string>('');
  const [loading, setLoading] = React.useState(false);

  const projectsFetchGenRef = React.useRef(0);
  const activeProjectIdForSubRef = React.useRef<string>('');

  // Use prop if provided, otherwise use internal state
  const activeProjectId = selectedProjectId || internalSelectedProjectId;
  activeProjectIdForSubRef.current = activeProjectId;
  const [error, setError] = React.useState<string | null>(null);
  const [showTokenInput, setShowTokenInput] = React.useState(false);
  const [tokenInput, setTokenInput] = React.useState('');
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  React.useEffect(() => {
    if (guidedSetupActive) {
      setIsCollapsed(true);
    }
  }, [guidedSetupActive]);

  // Check for token on mount and log available keys
  React.useEffect(() => {
    const token = apiService.getCurrentAuthToken();
    if (!token) {
      console.warn('[ProjectSelector] No authentication token found');
      // Check what keys are available in localStorage
      try {
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) keys.push(key);
        }
        console.log('[ProjectSelector] Available localStorage keys:', keys);
        console.log('[ProjectSelector] To get your token:');
        console.log('1. Open lawgic-ai in your browser');
        console.log('2. Open Developer Tools (F12)');
        console.log('3. In Console, run: localStorage.getItem("auth_token_development") or localStorage.getItem("auth_token_production")');
        console.log('4. Copy the token value and paste it in the "Enter Token Manually" field below');
      } catch (e) {
        console.error('[ProjectSelector] Could not access localStorage:', e);
      }
    } else {
      console.log('[ProjectSelector] Found authentication token');
    }
  }, []);

  // Load projects when solution or mount changes
  const loadProjects = React.useCallback(async () => {
    const fetchGen = ++projectsFetchGenRef.current;
    const config = SOLUTION_CONFIG[solutionId];
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getProjects();
      if (fetchGen !== projectsFetchGenRef.current) {
        return;
      }
      if (response.success && response.data) {
        const filteredProjects = response.data.filter(
          (project) =>
            project.domain === config.domain && project.sub_domain === config.sub_domain
        );
        setProjects(filteredProjects);
      } else {
        setError('Failed to load projects');
      }
    } catch (err: any) {
      if (fetchGen !== projectsFetchGenRef.current) {
        return;
      }
      console.error('Error loading projects:', err);

      // Provide user-friendly error messages
      if (err.response?.status === 401) {
        setError('Authentication required. Please log in to the lawgic-ai application first, or ensure your authentication token is available.');
      } else if (err.response?.status === 404) {
        setError('Projects endpoint not found. Please verify the backend is running and the API URL is correct.');
      } else if (err.message?.includes('No projects endpoint available')) {
        setError('Unable to connect to projects API. Please check that the backend server is running.');
      } else {
        setError(err.message || 'Failed to load projects');
      }
    } finally {
      if (fetchGen === projectsFetchGenRef.current) {
        setLoading(false);
      }
    }
  }, [solutionId]);

  React.useEffect(() => {
    setProjects([]);
    setSubProjects([]);
    loadProjects();
  }, [loadProjects]);

  // Load sub-projects when project is selected or refreshTrigger changes
  React.useEffect(() => {
    if (activeProjectId) {
      loadSubProjects(activeProjectId);
    } else {
      setSubProjects([]);
    }
  }, [activeProjectId, refreshTrigger]);

  const loadSubProjects = async (projectId: string) => {
    const requestedProjectId = projectId;
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getSubProjects(projectId);
      if (requestedProjectId !== activeProjectIdForSubRef.current) {
        return;
      }
      if (response.success && response.data) {
        setSubProjects(response.data);
      } else {
        setError('Failed to load sub-projects');
      }
    } catch (err: any) {
      if (requestedProjectId !== activeProjectIdForSubRef.current) {
        return;
      }
      console.error('Error loading sub-projects:', err);
      setError(err.message || 'Failed to load sub-projects');
    } finally {
      if (requestedProjectId === activeProjectIdForSubRef.current) {
        setLoading(false);
      }
    }
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = e.target.value;
    if (!selectedProjectId) {
      // Only update internal state if prop is not provided
      setInternalSelectedProjectId(projectId);
    }
    onSubProjectSelect(''); // Clear sub-project selection
    if (onProjectSelect) {
      onProjectSelect(projectId);
    }
  };

  const handleSubProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '__create_new__' && onCreateNewSubProject) {
      e.target.value = selectedSubProjectId || '';
      onCreateNewSubProject();
      return;
    }
    onSubProjectSelect(value);
  };

  return (
    <div className={`project-selector ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="project-selector-header">
        <div className="project-selector-header-left">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="collapse-toggle-button"
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            <ChevronDown 
              size={18} 
              className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}
            />
          </button>
          <h3>Project Settings</h3>
        </div>
        <button
          onClick={() => loadProjects()}
          className="refresh-button"
          title={
            solutionId === 'master_cross_checks' || solutionId === 'construction_loan'
              ? 'Refresh projects'
              : 'Refresh playbooks'
          }
          disabled={loading}
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {!isCollapsed && (
        <>
          {error && (
            <div className="error-message">
              {error}
              {error.includes('Authentication required') && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  <p>To use the Clauses Editor, you need to authenticate:</p>
                  <ol style={{ marginLeft: '16px', marginTop: '4px' }}>
                    <li>Open the lawgic-ai web application in your browser</li>
                    <li>Log in to authenticate</li>
                    <li>Copy your authentication token from the browser's developer console</li>
                  </ol>
                  <button
                    onClick={() => setShowTokenInput(!showTokenInput)}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    {showTokenInput ? 'Hide' : 'Enter Token Manually'}
                  </button>
                  {showTokenInput && (
                    <div style={{ marginTop: '8px' }}>
                      <input
                        type="password"
                        value={tokenInput}
                        onChange={(e) => setTokenInput(e.target.value)}
                        placeholder="Paste authentication token here"
                        style={{
                          width: '100%',
                          padding: '6px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                      <button
                        onClick={() => {
                          if (tokenInput.trim()) {
                            apiService.setAuthToken(tokenInput.trim());
                            setTokenInput('');
                            setShowTokenInput(false);
                            setError(null);
                            loadProjects();
                          }
                        }}
                        style={{
                          marginTop: '4px',
                          padding: '6px 12px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          width: '100%'
                        }}
                      >
                        Save Token & Retry
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="project-selector-content">
            <div className="project-selector-left">
              <div className="select-group">
                <label htmlFor="project-select">Select {SOLUTION_CONFIG[solutionId].projectLabel}:</label>
                <select
                  id="project-select"
                  dir="auto"
                  value={activeProjectId}
                  onChange={handleProjectChange}
                  disabled={loading}
                >
                  <option value="">-- Select a {SOLUTION_CONFIG[solutionId].projectLabel.toLowerCase()} --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {activeProjectId && (
                <div className="select-group">
                  <label htmlFor="subproject-select">Select {SOLUTION_CONFIG[solutionId].subProjectLabel}:</label>
                  <select
                    id="subproject-select"
                    dir="auto"
                    value={selectedSubProjectId || ''}
                    onChange={handleSubProjectChange}
                    disabled={loading || !activeProjectId}
                  >
                    <option value="">-- Select a {SOLUTION_CONFIG[solutionId].subProjectLabel.toLowerCase()} --</option>
                    {onCreateNewSubProject && (
                      <option value="__create_new__">+ Create new...</option>
                    )}
                    {subProjects.map((subProject) => (
                      <option key={subProject.id} value={subProject.id}>
                        {subProject.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

        </>
      )}
    </div>
  );
};

